import { formatDistanceToNow, formatDistanceToNowStrict } from "date-fns";
import { ko } from "date-fns/locale";

/**
 * Convert an ISO-8601 timestamp (or Date) to a Korean relative-time string.
 *
 * Examples:
 *   relativeTime("2026-05-14T10:00:00+09:00")  → "5분 전"
 *   relativeTime(null)                          → "—"
 *   relativeTime(undefined)                     → "—"
 *
 * If `strict` is true, returns single-unit strict form (e.g. "5분 전" instead
 * of "약 5분 전") — preferred for compact UI rows.
 *
 * `referenceNow` lets callers compute relative time against the server's
 * "generatedAt" instead of the local clock when the API exposes a serverNow
 * field; this avoids drift if the user's machine has a wrong clock.
 */
export function relativeTime(
  input: string | Date | null | undefined,
  options: { strict?: boolean; referenceNow?: Date | string | number } = {},
): string {
  if (input == null) return "—";
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return "—";

  const { strict = true, referenceNow } = options;

  if (referenceNow != null) {
    const now =
      referenceNow instanceof Date ? referenceNow : new Date(referenceNow);
    if (!Number.isNaN(now.getTime())) {
      const diffMs = now.getTime() - date.getTime();
      return formatDuration(diffMs, strict);
    }
  }

  return strict
    ? formatDistanceToNowStrict(date, { addSuffix: true, locale: ko })
    : formatDistanceToNow(date, { addSuffix: true, locale: ko });
}

/**
 * Format a millisecond delta as a Korean relative-time string anchored to
 * "now" implicitly (the delta itself encodes that). Positive delta means
 * the timestamp is in the past.
 */
function formatDuration(diffMs: number, strict: boolean): string {
  const absSec = Math.round(Math.abs(diffMs) / 1000);
  const suffix = diffMs >= 0 ? "전" : "후";

  if (absSec < 60) return `${absSec}초 ${suffix}`;
  const min = Math.round(absSec / 60);
  if (min < 60) return `${min}분 ${suffix}`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 ${suffix}`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}일 ${suffix}`;

  // For longer spans fall back to date-fns for natural phrasing.
  const anchor = new Date(Date.now() - diffMs);
  return strict
    ? formatDistanceToNowStrict(anchor, { addSuffix: true, locale: ko })
    : formatDistanceToNow(anchor, { addSuffix: true, locale: ko });
}
